import queryString from 'query-string';
import {
  debounce,
  findLast,
  toLower,
  trim,
  includes,
  keys,
  isArray,
  isObject,
  omit,
  isNumber,
  remove,
  split,
  last
} from 'lodash-es';
import appStore from '@/stores';
import type { FormInstance } from 'antd';
import { Decimal } from 'decimal.js';
import storage from '@/utils/storage';
import { customAlphabet } from 'nanoid';

type KeyNameMap = {
  [key: string]: string,
}

export function convertMapToAntdOptions(map: KeyNameMap = {}) {
  return Object.keys(map).map((key) => ({
    label: map[key],
    value: key,
  }));
}

export function converOptionsAntdToMap(arr: Array<any>) {
  let map = {} as KeyNameMap;
  arr.forEach((d: any) => {
    map[d.value] = d.label;
  });
  return map;
}

export function filterOptionAntdSelect(input: string, option: any) {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
}

export function asyncDebounce(func: (...args: any[]) => Promise<any>, wait: number) {
  const debounced = debounce(async (resolve, reject, bindSelf, args) => {
    try {
      const result = await func.bind(bindSelf)(...args);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  }, wait);

  // This is the function that will be bound by the caller, so it must contain the `function` keyword.
  function returnFunc(...args: any) {
    return new Promise((resolve, reject) => {
      // @ts-ignore
      debounced(resolve, reject, this, args);
    });
  }
  return returnFunc;
}

export function getMenuName(url: string) {
  const menuItem = findLast(appStore.menuIdMap, function (item) {
    return item['menu_url'] == url; //TODO 有参数匹配
  });
  return menuItem?.[`menu_${toLower(appStore.locale)}`];
}

type fmsDownOptType = {
  newName?: string,//文件名称
  disableDownload?: boolean,//是否禁止下载
  thumbnail?: boolean,//是否获取压缩图片
  temporarySignature?:boolean,// 是有使用云链接
  region?: 'AU' | 'EU' | 'US',//区域
}
// fms图片/文件下载
export function getFmsImageUrl(url: string, opts: fmsDownOptType = {
  newName: '',
  disableDownload: false,
  thumbnail: false,
  temporarySignature:false,
}) {
  if (!url) return '';
  opts = (keys(omit(opts, 'encrypt')).filter((k: string) => opts[k] !== '')).reduce((obj: object, k: string) => {
    obj[k] = opts[k];
    return obj;
  }, {});
  return queryString.stringifyUrl({
    url: appStore.config.url.APP + '/fmsFileDownload',
    query: {
      url,
      ...opts,
    }
  });
}
type fileDownOptType = {
  fileName?: string,//文件名称
  encrypt?: boolean,//是否加密路径
  isDownloadPdf?: string,//是否直接下载pdf  Y:下载 N:浏览器直接打开
}
//文件服务器图片/文件下载
export function getImageDownloadUrl(url: string, useAbsolutePath: boolean = false, opts: fileDownOptType = {
  fileName: '',
  encrypt: false,
  isDownloadPdf: 'Y',//是否直接下载pdf  Y:下载 N:浏览器直接打开
}) {
  if (!url) return '';
  var imgUploadsUrlIndex = useAbsolutePath ? -1 : url.indexOf('/uploads');
  const query: any = opts.encrypt
    ? {
      encrypt: window.btoa(url.slice(imgUploadsUrlIndex + 1, url.length))
    }
    : {
      url: url.slice(imgUploadsUrlIndex + 1, url.length)
    };
  opts = (keys(omit(opts, 'encrypt')).filter((k: string) => opts[k] !== '')).reduce((obj: object, k: string) => {
    obj[k] = opts[k];
    return obj;
  }, {});

  return queryString.stringifyUrl({
    url: appStore.config.url.APP + '/fileDownload',
    query: {
      ...query,
      ...opts
    }
  });
}

export function splitStrByCommaAndNewline(str = ''): string[] {
  return str.split(/\n/).filter(trim).reduce((chars: string[], s: string) => {
    if (!includes(chars, s)) {
      chars.push(s);
    }
    return chars;
  }, []);
}

/** 清除表单的校验提示信息
 * @param formInstance // form实例
 * @param exceptFields // 需要排除的字段不清除提示，不传默认清除全部
 * **/
export function clearFormValidTip(formInstance: FormInstance, exceptFields?: string[]) {
  let fieldValus: any[] = [];
  keys(formInstance.getFieldsValue() || {})
    .filter((k: string) => exceptFields ? !includes(exceptFields, k) : k)
    .forEach((k: string) => {
      let value = formInstance.getFieldValue(k);
      if (isArray(value)) {
        value.forEach((d: any, index: number) => {
          if (isObject(d) && !isArray(d)) {
            keys(d).forEach((o: any) => {
              fieldValus.push({
                name: [k, index, o],
                value: d[o],
                errors: []
              });
            });
          }
        });
      } else {
        fieldValus.push({
          name: k,
          value: value,
          errors: []
        });
      }
    });
  formInstance.setFields(fieldValus);
}


export const createWindow = () => {
  const win = window.open();

  return {
    open(url: string) {
      if (win) {
        win.location.href = url;
        window.setTimeout(() => {
          win && win.close();
        }, 5000);
      }
    },
  };
};

/** 保留xx小数 清除多余的0
 * @param num // 数字
 * @param fixed // 要保留几位小数
 * **/
export const removeExtraZeroes = (num: any, fixed = 2) => {
  let str = '';
  let realNum = num * 1;
  if (isNumber(realNum) && !isNaN(realNum)) {
    str = (realNum).toFixed(fixed);
  }
  return str.replace(/\.?0+$/, '');
};

/**
 *
 * @param 根据度量磅转千克
 * @returns
 * @description: 传参接口使用，由unit确定weight值单位，是英制单位则磅转千克
 */
export const getlbtokgUnit = (weight: any) => {
  const unit = appStore?.config?.measurementSystem;
  let newWeight;
  if (unit == 'TBS') {//英制
    newWeight = removeExtraZeroes(weight * 0.4536, 4);
  } else {//公制
    newWeight = removeExtraZeroes(weight, 6);
  }
  return newWeight;
};

/**
 *
 * @param 根据度量千克转磅
 * @returns
 * @description: 页面显示使用，传入weight值是公制单位kg，若当前偏好配置英制则千克转磅
 */
export const getkgtolbUnit = (weight: any) => {
  const unit = appStore?.config?.measurementSystem;
  let newWeight;
  if (unit == 'TBS') {
    newWeight = removeExtraZeroes(weight / 0.4536, 4);
  } else {
    newWeight = removeExtraZeroes(weight, 6);
  }
  return newWeight;
};

/**
 *
 * @param 根据度量英寸转厘米
 * @returns
 */
export const getInchtoCMUnit = (length: any) => {
  const unit = appStore?.config?.measurementSystem;
  let newLength;
  if (unit == 'TBS') {
    newLength = removeExtraZeroes(length * 2.54, 2);
  } else {
    newLength = removeExtraZeroes(length, 2);
  }
  return newLength;
};
/**
 *
 * @param 根据度量厘米转英寸
 * @returns
 */
export const getCMtoInchUnit = (length: any) => {
  const unit = appStore?.config?.measurementSystem;
  let newLength;
  if (unit == 'TBS') {
    newLength = removeExtraZeroes(length / 2.54, 2);
  } else {
    newLength = removeExtraZeroes(length, 2);
  }
  return newLength;
};

/**
  * 根据立方米转立方英寸
  * @param type volume
  * @return float
  */
export const getCBMtoCuinUnit = (volume: any) => {
  const unit = appStore?.config?.measurementSystem;
  if (!isNumber(volume) || isNaN(Number(volume))) return '';

  if (unit === 'TBS') {
    return Decimal.mul(volume, 61023).toFixed(9);;
  }

  return new Decimal(volume).toFixed(9);;
};
/**
  * 根据立方英寸转立方米
  * @param type volume
  * @return float
  */
export const getCuinToCBMUnit = (volume: any) => {
  const unit = appStore?.config?.measurementSystem;
  if (!isNumber(volume) || isNaN(Number(volume))) return '';

  if (unit === 'TBS') {
    return Decimal.mul(volume, 1/61023).toFixed(9);;
  }

  return new Decimal(volume).toFixed(9);;
};
/**
 * 去除链接中的域名
 * @param 链接
 * @returns
 */
export const removeDomainFromUrl = (url: string) => {
  // 尝试解析URL
  try {
    const parsedUrl = new URL(url);
    // 如果协议和主机名存在，说明URL包含域名
    if (parsedUrl.protocol && parsedUrl.hostname) {
      // 返回路径、查询字符串和哈希，不包括域名
      return parsedUrl.pathname.replace(/^\//, "") + parsedUrl.search + parsedUrl.hash;
    }
  } catch (error) {
    // 如果URL格式不正确，则返回原始字符串
    return url;
  }
  // 如果URL没有域名（可能是相对路径），直接返回原始字符串
  return url;
};

/**
 * 生成一个全局唯一标识符（GUID）的函数。
 * 该函数没有参数。
 * @returns {string} 返回一个格式为 xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx 的全局唯一标识符。
 */
export const guid = () => {
  // 生成一个四位的16进制字符串
  const S4 = () => {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  // 通过组合四个四位16进制字符串，并以特定格式连接它们来生成GUID
  return (S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4());
};

export const removeExtraZeros = (number: number | string) => {
  if (!Number(number) || !number) return number;
  return (+number)?.toFixed(12)?.toString()?.replace(/(?:\.0*|(\.\d+?)0+)$/, '$1');
};

// 计算需要合并的单元格
export const mergeCell = (list: any[], mergeKey: string, addKey: string = '_rowSpan') => {
  let tmp;

  for (const item of list) {
    if (!tmp) {
      tmp = item;
      tmp[addKey] = 1;
      continue;
    }

    if (tmp[mergeKey] === item[mergeKey]) {
      tmp[addKey] = (tmp[addKey] || 0) + 1;
      item[addKey] = 0;
    } else {
      tmp = item;
      tmp[addKey] = 1;
    }
  }

  return list;
};

/** 移动数组项位置 - 原数组不变 */
export function arrayMove(array: any[], fromIndex: number, toIndex: number) {
  const newArray = [...array];
  const startIndex = fromIndex < 0 ? newArray.length + fromIndex : fromIndex;
  if (startIndex >= 0 && startIndex < newArray.length) {
    const endIndex = toIndex < 0 ? newArray.length + toIndex : toIndex;
    const [item] = newArray.splice(fromIndex, 1);
    newArray.splice(endIndex, 0, item);
  }
  return newArray;
}

// 移除文件扩展名
export const removeFileExtension = (filename: string) => {
  // 查找最后一个点（.）的位置，这通常是文件扩展名的开始
  const dotIndex = filename.lastIndexOf('.');

  // 如果找到了点（.），则截取从文件开始到点之前的部分
  if (dotIndex > 0) {
    return filename.slice(0, dotIndex);
  }

  // 如果没有找到点（.），则可能是没有扩展名的文件名，直接返回原文件名
  return filename;
};

export function fuzzySearch(input: any, option: any) {
  // 获取选项中的文本
  const { label } = option;
  // 将输入转换为小写，并移除前后空格
  const lowerCaseInput = input?.toLowerCase()?.trim();
  // 将选项文本转换为小写，并移除前后空格
  const lowerCaseOption = label?.toLowerCase()?.trim();

  // 实现左模糊精确匹配逻辑
  return lowerCaseOption?.startsWith?.(lowerCaseInput);
}


/**
 * 等待
 * @param delay millisecond 毫秒时间
 */
export const wait = async (delay: number) => new Promise((resolve) => {
  setTimeout(resolve, delay);
});

//数字转千分位
export function covertToThousandths(val: any) {
  return isNumber(val) ? new Intl.NumberFormat().format(val) : val;
}

//数字转千分位，超过9999显示9,999+
export function formatNumberWithComma(n: number | string, limit = 9999) {
  const num = Number(n);
  if (!isNaN(num)) {
    return covertToThousandths(num > limit ? limit : num)+(num > limit? '+':'');
  } else {
    return n;
  }
}

//
/**
 *延迟50毫秒 滚到到指定位置
 * @param containerSelector 容器选择器
 * @param targetSelector 需要滚到动的选择器
 * @param offset 偏移距离 默认 40
 * @param behavior 滚动行为 默认 smooth
 */
export const scrollToBehavior = async (containerSelector: string, targetSelector: string,
  offset: number = 40, behavior: ScrollBehavior | undefined = 'smooth') => {
  await wait(50);
  const container = document.querySelector(containerSelector);
  if (container) {
    const targetDom = container.querySelector(targetSelector);
    if (targetDom) {
      container.scrollTo({
        top: container.scrollTop - (container.getBoundingClientRect().top - targetDom.getBoundingClientRect().top) - offset,
        behavior: behavior
      });
    }
  }
};

// 假设你想要让下拉框始终相对于 Select 所在的 div 定位
export const getPopupContainer = (triggerNode: { parentNode: any; }) => {
  // 这里返回的是触发下拉框的 Select 节点最近的定位父元素
  // 如果你的 Select 没有直接父元素定位，你可以返回一个固定的容器
  return triggerNode.parentNode; // 或者你可以返回一个固定的 DOM 元素，比如 document.body
};

export const getFmsAllUrl = (url:string)=>{
  let fileUrl = '';
  if (url.indexOf('https://') == -1) {
    fileUrl = appStore.config.url.FMS + (url.charAt(0) == '/' ? '' : '/') + url;
  } else {
    const parser = document.createElement('a');
    parser.href = url;
    fileUrl = appStore.config.url.FMS + '/' + parser.pathname.substr(1);
  }
  return fileUrl;
};
export const getFmsAllUrlEu = (url:string)=>{
  let fileUrl = '';
  if (url.indexOf('https://') == -1) {
    fileUrl = appStore.config.url.FMS_EU + (url.charAt(0) == '/' ? '' : '/') + url;
  } else {
    const parser = document.createElement('a');
    parser.href = url;
    fileUrl = appStore.config.url.FMS_EU + '/' + parser.pathname.substr(1);
  }
  return fileUrl;
};

/**
 * 获取 fms 或 ftp 文件下载地址
 * @param url
 * @fmsOpts fms 额外参数
 * @isBigFile 是否大文件(大文件直接返回url，华为云/亚马逊云地址)
 * @returns
 */
export const getFileUrl = (url: string,fmsOpts: fmsDownOptType = {
  newName: '',
  disableDownload: false,
  thumbnail: false
}) => {
  if(!url){
    return '';
  }
  if (isFmsFile(url)) { return getFmsImageUrl(url, fmsOpts); };

  const isAbsolutePath:boolean = /^https?:\/\//.test(url);
  const isFilePath:boolean = /^https?:\/\/files\.winit\.com/.test(url);
  const regionList: string[] = ['EU', 'US', 'AU', 'CN'];
  //判断url是否为相对链接,不存在fms方法
  if(isAbsolutePath){ //绝对路径
    var filePattern = /https?:\/\/(\w+)file\.winit\.com/;
    var fileMatches = url.match(filePattern);

    if(isFilePath){ //非FMS且是files
      return getImageDownloadUrl(url, /^https?:\/\//.test(url));

    } else {
      if (fileMatches) {
        var fileRegion = fileMatches[1].toUpperCase();
        if (includes(regionList,fileRegion)) {
          return /^http:\/\//.test(url) ? url.replace('http://', 'https://') : url;
        }
      }
      return url;
    }
  }else{//相对路径
    if(url.indexOf('/uploads/') !== -1){
      return getImageDownloadUrl(url, /^https?:\/\//.test(url));
    }
  }

  return url;
};
/**
 * 文件是否为fms
 * @param url
 * @returns
 */
export const isFmsFile = (url:string) => {
  if (!url) return false;
  const isAbsolutePath:boolean = /^https?:\/\//.test(url);
  const regionList:string[] = ['EU', 'US', 'AU', 'CN'];
  //判断url是否为相对链接,不存在fms方法
  if(isAbsolutePath){ //绝对路径
    var pattern = /https?:\/\/(\w+)fmsstream\.winit\.com/;
    var matches = url.match(pattern);

    if ((appStore?.config?.fmsUrlList || []).some(function (fmsUrl) { return url.indexOf(fmsUrl) === 0; })
    ) {
      return true;
    }
    if (matches) { //FMS
      var region = matches[1].toUpperCase();
      if (includes(regionList, region)) {
        return true;
      }
    }
  }else{//相对路径
    if(url.indexOf('/uploads/') == -1){
      return true;
    }
  }

  return false;

};
/**用户使用习惯缓存场景定义**/
const userUsageStorageConfig:{[key:string]:{recordsMax:number,desc:string}} = {
  'mmsBatchPrintBarcodesSetting': {
    recordsMax: 9,
    desc: '批量打印条码-打印文件生成设置/其他打印项选择记录'
  },
  'imsCountryTabsClickRecords': {
    recordsMax: 31,
    desc: '海外仓库存-国家查询标签点击记录'
  },
  'imsWarehouseTabsClickRecords': {
    recordsMax: 31,
    desc: '海外仓库存-仓库查询标签点击记录'
  },
  'imsConditionQueryType': {
    recordsMax: 19,
    desc: '海外仓库存/分类库存/库存流水-商品编码/条码查询类型选择记录'
  },
  'mmsConditionQueryType': {
    recordsMax: 19,
    desc: '商品信息-商品编码/条码/第三方商品条码查询类型选择记录'
  }
} as const;

type storageKeyType = keyof typeof userUsageStorageConfig;

/**缓存最新指定次数的数据（记录时间戳的方式），用于记录用户习惯
 * @param storageKey 缓存的key
 * @param recordsMax 保存最新的次数
 * @param activeValueKey 准备缓存的数据当前操作的key
 * **/
export const setStorageCount = (
  storageKey: storageKeyType | string,
  activeValueKey: string,
  recordsMax?: number,
) => {
  const timeStamp = new Date().getTime();
  let storageData = storage.get(storageKey as string) ?? {};
  let recordsSum = Object.keys(storageData).reduce((sum: any, k: any) =>sum + (storageData[k]?.length ?? 0), 0);
  //超过指定数量，删掉最早存入的时间戳
  if (!recordsMax) {
    recordsMax = userUsageStorageConfig[storageKey]?.recordsMax;
  }
  if (recordsSum >= recordsMax) {
    let minTimeStamp = 0, minTimeStampKey = '';
    Object.keys(storageData).forEach((key) => {
      if (storageData[key]?.length) {
        if (minTimeStampKey === '') {
          minTimeStamp = storageData[key][0];
          minTimeStampKey = key;
        }
        let min = Math.min(...storageData[key]);
        if (min < minTimeStamp) {
          minTimeStamp = min;
          minTimeStampKey = key;
        }
      }
    });
    if (minTimeStampKey && minTimeStamp) {
      remove(storageData[minTimeStampKey],(v)=>v===minTimeStamp);
    }
  }
  let keys = activeValueKey.split('.');
  if (keys.length > 1) {
    if (!storageData[keys[0]]) { storageData[keys[0]] = {}; };
    let arr = [...(storageData[keys[0]][keys[1]]??[])];
    arr.push(timeStamp);
    storageData[keys[0]][keys[1]] = arr;
  } else {
    let arr = [...(storageData[activeValueKey]??[])];
    arr.push(timeStamp);
    storageData[activeValueKey] = arr;
  }
  storage.set(storageKey as string, storageData);
};

export const getEmailDomain = (email:string) => {
  const atIndex = email.indexOf('@');
  if (atIndex !== -1) {
    return email.slice(atIndex + 1);
  }
  return '';
};
//处理[0,99999]内数学数值转为到中文简体数值
export const numberToChinese = (num: number) => {
  if (appStore.config.language === 'en-us') {
    return num;
  }
  const chineseNumbers = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const chineseUnits = ['', '十', '百', '千', '万'];
  if (num === 0) {
    return chineseNumbers[0];
  }
  let result = '';
  let strNum = num.toString();
  let length = strNum.length;
  for (let i = 0; i < length; i++) {
    let digit = parseInt(strNum[i]);
    let unit = length - i - 1;
    if (digit === 0) {
      // 处理连续的零
      if (result[result.length - 1] !== chineseNumbers[0]) {
        result += chineseNumbers[digit];
      }
    } else {
      result += chineseNumbers[digit] + chineseUnits[unit];
    }
  }
  // 处理以“一十”开头的特殊情况
  if (result.startsWith('一十')) {
    result = result.substring(1);
  }
  // 去除末尾的零
  result = result.replace(/零+$/, '');
  return result;
};
/**
 * 生成一个UUID
 * @param prefix UUID的前缀，可选参数，默认为空字符串
 * @param digit UUID的长度，可选参数，默认为32
 * @param character 用于生成UUID的字符集，可选参数，默认数字大小写字母
 * @returns 生成的UUID字符串
 */
export const getuuid = (prefix?: string, digit?: number, character?: string) => {
  const defCharacter = "1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const defDigit = 32;
  const nanoid = customAlphabet(character ?? defCharacter, digit ?? defDigit);
  return (prefix ? `${prefix}_` : '') + nanoid();
};

/**
 * 从给定的 URL 或文件路径字符串中提取文件名。
 *
 * 通过正则分割字符串（支持斜杠和反斜杠），获取最后一段，并去除查询参数或哈希片段。
 *
 * @param url - 要提取文件名的 URL 或文件路径。
 * @returns 提取到的文件名，如果输入为空或无效则返回 undefined。
 */
export const extractFilename = (url:string) => {
  return last(split(url, /[\\/]/))?.replace(/[?#].*$/, '');
};

/**
 * 方法用于打开一个新的浏览器窗口或查找一个已命名的窗口,处理异步打开新窗口个别浏览器拦截问题
 * @param url 打开指定的页面的URL
 * @param target 指定target属性或窗口的名称
 */
export const openWindow = (url: string, target = '_blank', ...arg: any) => {
  setTimeout(() => window.open(url, target, ...arg));
};
/**
 * 规则引擎构建树结构
 * @param data
 * @returns
 */

export const getOptionItem = (data: any, constant: { labelKey: string; valueKey: string, childrenKey: string; }) => {
  if (typeof data === 'object') {
    const option: any = {
      label: data[constant.labelKey || 'label'],
      value: `${data[constant.valueKey || 'value']}`,
      key: data[constant.valueKey || 'value'],
    };
    const childrenKey = constant.childrenKey || 'children';
    if (Reflect.has(data, childrenKey)) {
      option.children = (data[childrenKey] || []).map((item:any) => getOptionItem(item, constant));
    }
    return option;
  }
  return {
    label: data,
    value: data,
    key: data,
  };
};

type TypeMap = Record<string, string>;
type TypeTreeSelect = { title: string, value: string, children?: TypeTreeSelect[] };
type TypeKeyMap = { title: string, value: string, children: string };

export function getTreeTransformData(data: any[], keyMap: TypeKeyMap = {
  title: 'sceneName',
  value: 'sceneCode',
  children: 'children'
}) {
  if (!Array.isArray(data) || data.length === 0) {
    return {
      options: [],
      map: {}
    };
  }
  const { title, value, children } = keyMap;
  const map:TypeMap = {};

  function transformNodes(items:TypeTreeSelect[]) {
    return items.map((item:any) => {
      map[item[value]] = item[title];
      const node = {
        value: item[value],
        title: item[title],
        key: item[value],
        children:[] as TypeTreeSelect['children']
      };

      if (item[children] && Array.isArray(item[children]) && item[children].length > 0) {
        node.children = transformNodes(item[children]);
      }

      return node;
    });
  }

  const options: TypeTreeSelect[] = transformNodes(data);

  return {
    options,
    map
  };
}
